import { Button, Menu, MenuProps } from "antd";
import {
    BuildingsIcon, CubeIcon, CurrencyCircleDollarIcon, DotsNineIcon, EyeIcon, EyeSlashIcon,
    FadersIcon, FireIcon, GlobeIcon, GlobeHemisphereWestIcon, GoogleLogoIcon, HashStraightIcon,
    HouseSimpleIcon, ImagesIcon, LightningIcon, MapPinIcon, MapTrifoldIcon, PackageIcon, PencilLineIcon, PlusIcon,
    QuestionIcon, ShuffleIcon, SolarRoofIcon, SquaresFourIcon, StackIcon, SunIcon, SunHorizonIcon,
    TreeEvergreenIcon, UserCircleIcon
} from "@phosphor-icons/react";
import React, { useContext, useEffect, useState } from "react";
import { LAYER_OPTIONS } from "../../../../Constants";
import { useViewerStore } from "../../../../store/store";
import { LoadingContext } from "../../../../store/loadingMessageStore";
import { useCurrentUser } from "../../../../contexts/UserContext";
import { ThemeColorContext } from "../../../../contexts/ThemeColorContext";
import { LoadModelJson, LoadModelXML } from "../Three-JS-Viewer/Controllers/Loader";
import AuroraIcon from '../../../Pure-UI-Components/Icons/AuroraIcon';
import EagleViewIcon from '../../../Pure-UI-Components/Icons/EagleViewIcon';
import ProductionEstimateForm from "../Modal/Modal-Content/ProductionEstimateForm";
import { toggleLayer } from "../../../../helpers/storeHelpers";
import { ImageryOptions, TabName } from "../../../../@types/enums";
import { LayerName } from "../../../../@types/store";
import { SelectedDesignVendorType } from "../../../../@types/types";
import { MenuItemType } from "antd/es/menu/interface";

const Menubar: React.FC = () => {
    const pathname = useViewerStore(state => state.pathname);
    const { layerVisibilityState, sceneViewer, siteDesigns, selectedSiteDesign, imageryType } = useViewerStore();
    const setIsSidebar = (val: boolean) => useViewerStore.setState({ isLeftSidebarOpen: val });
    const isStreetViewVisible = useViewerStore((state) => state.isStreetViewVisible);

    const { hasCapability } = useCurrentUser();
    const { primary } = useContext(ThemeColorContext);

    const [currentMenu, setCurrentMenu] = useState('roof');
    const [currentTool, setCurrentTool] = useState([]);
    const stage = Number(sessionStorage.getItem('currentStage')) ?? 0;
    const subStage = sessionStorage.getItem('stageStatus');

    const handleToolClick = () => {
        setCurrentTool([]);
    };

    const handleMenuClick: MenuProps['onClick'] = (e) => {
        setCurrentMenu(e.key);
        sceneViewer.setMenu(e.key);
        if (e.key === 'obstruction' || e.key === 'tree' || e.key === 'building' || e.key === 'roof') {
            const tabName = String(e.key[0]).toUpperCase() + String(e.key).slice(1);
            sceneViewer.setTabInfo(`${tabName} Properties`);
        }
    };

    useEffect(() => {
        if (pathname === '/configure/building') {
            setCurrentMenu('building');
        } else if (pathname === '/configure/roof') {
            setCurrentMenu('roof');
        } else if (pathname === '/configure/obstruction') {
            setCurrentMenu('obstruction');
        } else if (pathname === '/configure/tree') {
            setCurrentMenu('tree');
        } else if (pathname === '/configure/modules') {
            setCurrentMenu('modules');
        } else if (pathname === '/configure/solar-access') {
            setCurrentMenu('solar-access');
        } else if (pathname === '/configure/irradiance') {
            setCurrentMenu('irradiance');
        } else if (pathname === '/configure/price-calculation') {
            setCurrentMenu('epc');
        } else if (pathname === '/configure/what-if') {
            setCurrentMenu('what-if');
        } else if (pathname === '/configure/building/engineering-letter') {
            setCurrentMenu('');
        } else if (pathname === '/configure/building/engineering-affidavit') {
            setCurrentMenu('');
        } else if (pathname === '/configure/packing-slip') {
            setCurrentMenu('packing-slip');
        }
    }, [pathname]);

    const handleCalculateRoofSquare = () => {
        sceneViewer.setMenu('roof');
        setTimeout(() => {
            document.getElementById('roofSquareTabIcon')?.click();
            document.getElementById('roofSquareTableButton')?.click();
        }, 1000);

    }

    const handleToggleLayer = (layerName: LayerName) => {
        const previousState = layerVisibilityState[layerName];
        toggleLayer(layerName);
        sceneViewer.setVisibleGroup(layerName, !previousState);
    };

    const handleChangeImagery = (imagery: ImageryOptions) => {
        useViewerStore.setState({ imageryType: imagery });
        sceneViewer.setMenu('custom-image');
    };

    const getFileExtension = (filename: string) => {
        return filename.slice((filename.lastIndexOf(".") - 1 >>> 0) + 2).toLowerCase();
    }


    const selectSiteDesign = async (type: SelectedDesignVendorType) => {
        if (type === selectedSiteDesign) {
            return;
        }

        useViewerStore.setState({ designID: 0, selectedDesignIndex: -1 });

        switch (type) {
            case 'aurora':
                await LoadModelJson(sceneViewer, siteDesigns.aurora);
                break;

            case 'eagleView':
                await LoadModelXML(sceneViewer, siteDesigns.eagleView);
                break;
        }

        useViewerStore.setState({ selectedSiteDesign: type });

    }

    const readUploadedFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        //Get File
        const file = e.target.files?.[0];
        if (!file) return;

        //Instantiate FileReader
        const reader = new FileReader();

        //Read File
        reader.readAsText(file);

        //Async function on file load
        reader.onload = async (e) => {
            //Get File Extension
            const fileExtension = getFileExtension(file.name);
            //File Content
            const fileContent = e.target?.result;

            //Check File Extension and call appropriate loader from sceneViewer
            if (fileExtension === 'json') {
                //Load JSON roof
                await LoadModelJson(sceneViewer, fileContent, null, true);

                if (file.name.split("_")) {
                    const site_address_arr = file.name.split("_")
                    let siteAddressString = ""

                    site_address_arr.forEach((word: string) => {
                        siteAddressString = siteAddressString + " " + word
                    })

                    let site_address_without_dot_json_arr = siteAddressString.split(".")

                    let finalAddress = ""

                    site_address_without_dot_json_arr.forEach((word) => {
                        if (word !== "json") {
                            finalAddress = finalAddress + " " + word
                        }
                    })

                    useViewerStore.setState({ siteAddress: finalAddress });
                }
            } else if (fileExtension === 'xml') {

                const loadingContext = new LoadingContext();

                //Load XML roof
                loadingContext.setMessage({ message: 'Reading XML file...' });

                await LoadModelXML(sceneViewer, fileContent);

                loadingContext.dispose()
            }
            sceneViewer.setMenu("roof");
            useViewerStore.setState({ selectedSiteDesign: 'custom' });
        };
    }

    const fileUploadHandler = () => {
        let fileInput = document.getElementById('fileInput');
        fileInput?.click();
    }

    const handleOnClickProductionEstimate = () => {
        sceneViewer.viewerAlert({
            show: true,
            title: "Production Estimate",
            message: <ProductionEstimateForm />,
            messageType: "info",
            isModal: true
        })
    }

    const sidebarItems = [
        {
            key: 'ModelSite',
            title: 'Model Site',
            icon: <span
                title={"Edit"}
                className={"left-menu-icon-wrapper"}
            >
                <SquaresFourIcon size={28} weight='fill' />
            </span>,
            children: [
                (hasCapability('View_Building_Attributes') &&
                {
                    label: 'Building (B)',
                    key: 'building',
                    icon: <BuildingsIcon size={24} weight='fill' />
                }

                ),
                {
                    label: 'Roof (R)',
                    key: 'roof',
                    icon: <HouseSimpleIcon size={24} weight='fill' />
                },
                {
                    label: 'Obstruction (O)',
                    key: 'obstruction',
                    icon: <CubeIcon size={24} weight='fill' />
                },
                {
                    label: 'Tree (T)',
                    key: 'tree',
                    icon: <TreeEvergreenIcon size={24} weight='fill' />
                },
            ],
        },
        {
            key: 'SystemDesign',
            icon:
                <span
                    title={"System Design"}
                    className={"left-menu-icon-wrapper"}
                >
                    <SunHorizonIcon size={28} weight='fill' />
                </span>
            ,
            children: [
                {
                    label: 'Modules (M)',
                    key: 'modules',
                    icon: <DotsNineIcon size={24} weight='bold' />
                },
                (hasCapability('Shade_Points') &&
                {
                    label: 'Shade Point (S)',
                    key: 'solar-access',
                    icon: <ShuffleIcon size={24} weight='fill' />
                }
                ),
                (hasCapability('Irradiance') &&
                {
                    label: 'Irradiance (I)',
                    key: 'irradiance',
                    icon: <SunIcon size={24} weight='fill' />
                }
                )
            ],
        },
        {
            key: 'PowerCalculation',
            icon:
                <span
                    title={"Power+Pricing"}
                    className={"left-menu-icon-wrapper"}
                >
                    <LightningIcon size={28} weight='fill' />
                </span>
            ,
            children: [
                {
                    label: 'What If (W)',
                    key: 'what-if',
                    icon: <QuestionIcon size={24} weight='fill' />
                },

                {
                    label: 'Production Estimate',
                    key: 'productionestinate',
                    icon: <LightningIcon size={24} weight='fill' />,
                    onClick: () => handleOnClickProductionEstimate()
                },

                (hasCapability('EPC_Calculations') &&
                {
                    label: 'EPC Calculation (E)',
                    key: 'epc',
                    icon: <CurrencyCircleDollarIcon size={24} weight='fill' />
                }
                )
            ],
        },
        (hasCapability('Surface_Draw') &&
        {
            key: 'AdvancedTools',
            icon:
                <span
                    title={"Ground Mount"}
                    className={"left-menu-icon-wrapper"}
                >
                    <PencilLineIcon size={28} weight='fill' />
                </span>
            ,
            children: [

                {
                    label: 'Ground Mount (F)',
                    key: 'surface-draw',
                    icon: <GlobeIcon size={24} weight='fill' />
                }
                ,
            ],
        }
        )

    ];

    let toolbarItems = [
        (hasCapability('Import_Design') &&
        {
            key: 'addDesign',
            icon:
                <span
                    title={"Site Designs"}
                    className={"left-menu-icon-wrapper"}
                >
                    <FadersIcon size={26} weight='fill' />
                </span>
            ,
            children: [
                {
                    label: <div style={{ color: selectedSiteDesign === 'custom' ? primary : undefined }}>Add Design</div>,
                    key: 'addDesignBtn',
                    icon: <PlusIcon size={24} weight='bold' />,
                    onClick: fileUploadHandler
                },
                {
                    label: <div style={{ color: selectedSiteDesign === 'aurora' ? primary : undefined }}>Aurora</div>,
                    key: 'useAuroraDesignBtn',
                    icon: <AuroraIcon size={24} />,
                    disabled: siteDesigns.aurora == null,
                    onClick: () => selectSiteDesign('aurora')
                },
                {
                    label: <div style={{ color: selectedSiteDesign === 'eagleView' ? primary : undefined }}>EagleView</div>,
                    key: 'useEagleViewDesignBtn',
                    icon: <EagleViewIcon size={24} />,
                    disabled: siteDesigns.eagleView == null,
                    onClick: () => selectSiteDesign('eagleView')
                }
            ]
        }
        )
        ,
        (hasCapability('Edit_Imagery') &&
        {
            key: 'map',
            icon: <span title={"Map Imagery"} className={"left-menu-icon-wrapper"}>
                <MapTrifoldIcon size={26} weight='fill' />
            </span>,
            children: [
                {
                    type: 'group',
                    label: 'Map Services',
                    children: [
                        {
                            label: (
                                <span
                                    id={`${ImageryOptions.GOOGLE}ID`}
                                    className={`imagery-label ${imageryType === ImageryOptions.GOOGLE ? 'active' : ''}`}
                                >Google</span>
                            ),
                            key: ImageryOptions.GOOGLE,
                            icon: <GoogleLogoIcon size={24} weight='fill' />,
                            onClick: () => handleChangeImagery(ImageryOptions.GOOGLE),
                        },
                        {
                            label: (
                                <span
                                    id={`${ImageryOptions.MAPBOX}ID`}
                                    className={`imagery-label ${imageryType === ImageryOptions.MAPBOX ? 'active' : ''}`}
                                >Mapbox</span>
                            ),
                            key: ImageryOptions.MAPBOX,
                            icon: <MapPinIcon size={24} weight='fill' />,
                            onClick: () => handleChangeImagery(ImageryOptions.MAPBOX),
                        },
                        {
                            label: (
                                <span
                                    id={`${ImageryOptions.NEARMAP}ID`}
                                    className={`imagery-label ${imageryType === ImageryOptions.NEARMAP ? 'active' : ''}`}
                                >Nearmap</span>
                            ),
                            key: ImageryOptions.NEARMAP,
                            icon: <GlobeHemisphereWestIcon size={24} weight='fill' />,
                            onClick: () => handleChangeImagery(ImageryOptions.NEARMAP),
                        },
                    ],
                },
                {
                    type: 'group',
                    label: 'Others',
                    children: [
                        (
                            hasCapability('Edit_Imagery') &&
                            {
                                label: (
                                    <span
                                        id={`${ImageryOptions.CUSTOM}ID`}
                                        className={`imagery-label ${imageryType === ImageryOptions.CUSTOM ? 'active' : ''}`}
                                    >Custom Imagery</span>
                                ),
                                key: ImageryOptions.CUSTOM, //'customimagery',
                                icon: <ImagesIcon size={24} weight='fill' />,
                                onClick: () => handleChangeImagery(ImageryOptions.CUSTOM),
                            }
                        )
                    ],
                }
            ]
        }
        )
        ,
        (hasCapability('Layers') && {
            key: 'layers',
            icon:
                <span
                    title={"Layers"}
                    className={"left-menu-icon-wrapper"}
                >
                    <StackIcon size={26} weight='fill' />
                </span>
            ,
            children: [
                {
                    type: 'group',
                    label: 'Model',
                    className: 'sidebar__layer',
                    children: [
                        {
                            label: (
                                <div
                                    className='sidebar__layer--item'
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggleLayer(LAYER_OPTIONS.MODEL.ROOF);
                                    }}>
                                    <span>Roof</span>
                                    {layerVisibilityState.roof ? <EyeIcon size={24} weight='fill' /> : <EyeSlashIcon size={24} />}
                                </div>
                            ),
                            key: 'roof',
                            icon: <HouseSimpleIcon size={24} weight='fill' />,
                            onClick: () => handleToggleLayer(LAYER_OPTIONS.MODEL.ROOF),
                            selectable: false
                        },
                        {
                            label: (
                                <div
                                    className='sidebar__layer--item'
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggleLayer(LAYER_OPTIONS.MODEL.OBSTRUCTION);
                                    }}>
                                    <span>Obstruction</span>
                                    {layerVisibilityState.obstruction ? <EyeIcon size={24} weight='fill' /> :
                                        <EyeSlashIcon size={24} />}
                                </div>
                            ),
                            key: 'obstruction',
                            icon: <CubeIcon size={24} weight='fill' />,
                            onClick: () => handleToggleLayer(LAYER_OPTIONS.MODEL.OBSTRUCTION),
                            selectable: false
                        },
                        {
                            label: (
                                <div
                                    className='sidebar__layer--item'
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggleLayer(LAYER_OPTIONS.MODEL.TREE);
                                    }}>
                                    <span>Tree</span>
                                    {layerVisibilityState.tree ? <EyeIcon size={24} weight='fill' /> : <EyeSlashIcon size={24} />}
                                </div>
                            ),
                            key: 'tree',
                            icon: <TreeEvergreenIcon size={24} weight='fill' />,
                            onClick: () => handleToggleLayer(LAYER_OPTIONS.MODEL.TREE),
                            selectable: false
                        },
                        {
                            label: (
                                <div
                                    className='sidebar__layer--item'
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggleLayer(LAYER_OPTIONS.MODEL.SETBACK);
                                    }}>
                                    <span>Setbacks</span>
                                    {layerVisibilityState.setback ? <EyeIcon size={24} weight='fill' /> :
                                        <EyeSlashIcon size={24} />}
                                </div>
                            ),
                            key: 'setbacks',
                            icon: <FireIcon size={24} weight='fill' />,
                            onClick: () => handleToggleLayer(LAYER_OPTIONS.MODEL.SETBACK),
                            selectable: false
                        },
                        {
                            label: (
                                <div
                                    className='sidebar__layer--item'
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggleLayer(LAYER_OPTIONS.MODEL.PATHWAY);
                                    }}>
                                    <span>Pathways</span>
                                    {layerVisibilityState.pathway ? <EyeIcon size={24} weight='fill' /> :
                                        <EyeSlashIcon size={24} />}
                                </div>
                            ),
                            key: 'pathways',
                            icon: <HashStraightIcon size={24} weight='bold' />,
                            onClick: () => handleToggleLayer(LAYER_OPTIONS.MODEL.PATHWAY),
                            selectable: false
                        },
                    ],
                },
                {
                    type: 'group',
                    label: 'System',
                    className: 'sidebar__layer',
                    children: [
                        {
                            label: (
                                <div
                                    className='sidebar__layer--item'
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggleLayer(LAYER_OPTIONS.SYSTEM.MODULES);
                                    }}>
                                    <span>Modules</span>
                                    {layerVisibilityState.modules ? <EyeIcon size={24} weight='fill' /> :
                                        <EyeSlashIcon size={24} />}
                                </div>
                            ),
                            key: 'modules',
                            icon: <DotsNineIcon size={24} weight='bold' />,
                            onClick: () => handleToggleLayer(LAYER_OPTIONS.SYSTEM.MODULES),
                            selectable: false
                        },
                        {
                            label: (
                                <div
                                    className='sidebar__layer--item'
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggleLayer(LAYER_OPTIONS.SYSTEM.IRRADIANCE);
                                    }}>
                                    <span>Irradiance</span>
                                    {layerVisibilityState.irradiance ? <EyeIcon size={24} weight='fill' /> :
                                        <EyeSlashIcon size={24} />}
                                </div>
                            ),
                            key: 'irradiance',
                            icon: <SunHorizonIcon size={24} weight='fill' />,
                            onClick: () => handleToggleLayer(LAYER_OPTIONS.SYSTEM.IRRADIANCE),
                            selectable: false
                        },
                    ],
                },
                {
                    type: 'group',
                    label: 'Maps',
                    className: 'sidebar__layer',
                    children: [
                        {
                            label: (
                                <div
                                    className='sidebar__layer--item'
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggleLayer(LAYER_OPTIONS.MAPS.MAP);
                                    }}>
                                    <span>Maps</span>
                                    {layerVisibilityState.maps ? <EyeIcon size={24} weight='fill' /> : <EyeSlashIcon size={24} />}
                                </div>
                            ),
                            key: 'maps',
                            icon: <MapTrifoldIcon size={24} weight='bold' />,
                            onClick: () => handleToggleLayer(LAYER_OPTIONS.MAPS.MAP),
                            selectable: false
                        },
                        {
                            label: (
                                <div
                                    className='sidebar__layer--item'
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggleLayer(LAYER_OPTIONS.MAPS.TERRAIN);
                                    }}>
                                    <span>Terrain</span>
                                    {layerVisibilityState.terrain ? <EyeIcon size={24} weight='fill' /> :
                                        <EyeSlashIcon size={24} />}
                                </div>
                            ),
                            key: 'terrain',
                            icon: <GlobeHemisphereWestIcon size={24} weight='fill' />,
                            onClick: () => handleToggleLayer(LAYER_OPTIONS.MAPS.TERRAIN),
                            selectable: false
                        },
                    ],
                },
            ],
        }),
        (hasCapability('View_Roof_Square') && {
            key: 'roofsquare',
            icon:
                <span
                    title={"Show Roof Square"}
                    className={"left-menu-icon-wrapper"}
                >
                    <SolarRoofIcon size={26} weight='fill' />
                </span>
            ,
            children: [
                {
                    label: 'Show Roof Square',
                    key: 'calculate',
                    icon: <SolarRoofIcon size={24} weight='bold' />,
                    onClick: handleCalculateRoofSquare
                }
            ]
        })
    ];

    function notFalsy<T>(x: T | false | null | undefined): x is T {
        return Boolean(x);
    }

    useEffect(() => {
        const canvasContainer = document.getElementById('canvasContainer') as HTMLElement | null;
        if (!canvasContainer || isStreetViewVisible) return;
        const bc = document.body.classList; // Body classList
        const cc = canvasContainer.classList; // Canvas container classList
        if (pathname === '/configure/packing-slip') {
            // Reset all classes first
            bc.remove('multi-toolbar-open-toggle', 'multi-toolbar-open', 'toolbar-open');
            cc.remove('canvas-collapsed-multimenu-maintab', 'canvas-collapsed-multimenu', 'canvas-collapsed', 'canvas-expanded');
            cc.add('canvas-expanded');
            bc.add('packing_slip_button');
            canvasContainer.setAttribute('data-collapsed', 'false');
        } else {
            bc.remove('packing_slip_button');
        }
        setTimeout(() => {
            sceneViewer?.resizeCanvas();
        }, 500);
    }, [isStreetViewVisible, pathname]);

    return (
        <div className='sidebar'>
            <input accept={".json,.xml"} onChange={readUploadedFile} id={"fileInput"} hidden type={"file"} />
            <div className='sidebar__inner'>
                <Button
                    title={"Open Drawer"}
                    type='text'
                    icon={<UserCircleIcon size={28} weight='fill' />}
                    size='large'
                    onClick={() => setIsSidebar(true)}
                />
                <Menu
                    onClick={handleMenuClick}
                    selectedKeys={[currentMenu]}
                    mode='vertical'
                    items={sidebarItems.filter(notFalsy) as MenuItemType[]}
                    expandIcon={null}
                    triggerSubMenuAction='click'
                />
                <Button
                    className={`menu_button${currentMenu === "packing-slip" ? " active" : ""}`}
                    title={"Packing Slip"}
                    type='text'
                    icon={<PackageIcon size={28} weight='fill' />}
                    size='large'
                    onClick={() => {
                        setCurrentMenu('packing-slip');
                        sceneViewer.setMenu(TabName.PackingSlip);
                    }}
                />
            </div>

            <div className='sidebar__inner'>
                <Menu
                    onClick={handleToolClick}
                    selectedKeys={currentTool}
                    mode='vertical'
                    items={toolbarItems.filter(notFalsy) as MenuItemType[]}
                    expandIcon={null}
                    triggerSubMenuAction='click'
                />
            </div>
        </div>
    )
}

export default Menubar;